/**
 * Browser Activity Monitor for VibeLive (ES6 Module)
 * Detects tab/browser closure and notifies server to update member status
 * 
 * Auto-loaded by VibeLiveCore - no manual import needed
 */

import Fetch from './fetch.js';

// Internal state: currently active channel IDs (supports multiple concurrent rooms)
const activeChannelIds = new Set();

// Optional WS sender injected by vibelive-core when inside a channel.
// Signature: (channelId, stats) => boolean (true = sent via WS)
// Falls back to HTTP when null or WS send returns false.
let _mediaHeartbeatSender = null;

/**
 * Export: Inject a WS-backed sender for media heartbeats.
 * Called by vibelive-core after entering a channel. Pass null to revert to HTTP.
 * @param {Function|null} fn
 */
export function setMediaHeartbeatSender(fn) {
    _mediaHeartbeatSender = fn || null;
}

/**
 * Export: Called by VibeLive when entering a channel
 * @param {string} channelId - The channel ID being entered
 */
export function setActiveChannel(channelId) {
    activeChannelIds.add(channelId);
    _startMonitoring();
    console.debug(`bactivity: Monitoring channel ${channelId} (${activeChannelIds.size} active)`);
}

/**
 * Export: Called by VibeLive when exiting channel or logging out
 * @param {string} [channelId] - Specific channel to stop monitoring. If omitted, clears all.
 */
export function clearActiveChannel(channelId) {
    if (channelId) {
        activeChannelIds.delete(channelId);
        console.debug(`bactivity: Stopped monitoring channel ${channelId} (${activeChannelIds.size} active)`);
    } else {
        activeChannelIds.clear();
        console.debug('bactivity: Stopped monitoring all channels');
    }
    // When all channels are cleared, stop monitoring and tear down audio nodes
    if (activeChannelIds.size === 0) {
        _stopMonitoring();
        disconnectLocalAudio();
        for (const [key, node] of _remoteNodes) {
            if (node.source) { try { node.source.disconnect(); } catch (_) {} }
        }
        _remoteNodes.clear();
    }
}

/**
 * Send keepalive request to server to update member status
 */
function pingBactivityServer(channel_id, event_type) {
    if (!channel_id) {
        console.debug('bactivity: No channel_id, skipping ping');
        return;
    }
    
    // Use Fetch.recordBrowserActivity which handles authToken, contextId, and keepalive automatically
    Fetch.recordBrowserActivity(channel_id, event_type);
}

/**
 * Record event to localStorage for debugging
 */
let logCounter = 0;
function recordTabEvent(event_type) {
    // Send closure-related events to server for ALL active channels.
    // Multiple events fire during tab close (beforeunload -> pagehide -> unload);
    // sending all of them maximizes the chance that at least one reaches the server.
    const isServerEvent = event_type === 'PAGE_LOAD' || event_type === 'TAB_BEFORE_UNLOAD'
        || event_type === 'PAGE_HIDE' || event_type === 'PAGE_UNLOAD';
    if (isServerEvent && activeChannelIds.size > 0) {
        for (const channelId of activeChannelIds) {
            pingBactivityServer(channelId, event_type);
        }
    }
    
    // Always log locally for debugging
    logCounter++;
    const now = new Date();
    const timestamp = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}.${now.getMilliseconds().toString().padStart(3, '0')}`;
    const logs = JSON.parse(localStorage.getItem('tabClosureLogs') || '[]');
    logs.push({ timestamp, counter: logCounter, message: event_type, channel_ids: [...activeChannelIds] });
    
    // Keep only the last 50 entries
    if (logs.length > 50) logs.shift();
    localStorage.setItem('tabClosureLogs', JSON.stringify(logs));
}

/**
 * Load event logs from localStorage and record page load
 */
function loadLogs() {
    const logs = JSON.parse(localStorage.getItem('tabClosureLogs') || '[]');
    if (logs.length > 0) {
        const highestCounter = Math.max(...logs.map(log => log.counter || 0));
        logCounter = highestCounter;
    }
    recordTabEvent('PAGE_LOAD');
}

// ============================================================================
// Media Activity Monitoring
// ============================================================================

// AudioContext and analyser nodes — created lazily
let _audioCtx = null;
let _selfAnalyser = null;
let _selfSource = null;          // MediaStreamAudioSourceNode for local audio

// Remote members: Map<memberId, {source}> plus a shared analyser under key '__analyser__'
const _remoteNodes = new Map();

// Current video state (pushed from vibelive-core)
let _localVideoActive = false;
let _remoteVideoCount = 0;

// Per-minute accumulators
let _selfLevelSamples = [];
let _remoteLevelSamples = [];
let _selfVideoSeenThisMinute = false;
let _maxRemoteVideoThisMinute = 0;

let _samplerTimer   = null;
let _heartbeatTimer = null;

const SAMPLE_INTERVAL_MS    = 10_000; // 10 s between audio samples
const HEARTBEAT_INTERVAL_MS = 60_000; // 60 s heartbeat

function _getAudioCtx() {
    if (!_audioCtx || _audioCtx.state === 'closed') {
        _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (_audioCtx.state === 'suspended') {
        _audioCtx.resume().catch(() => {});
    }
    return _audioCtx;
}

function _makeAnalyser(ctx) {
    const a = ctx.createAnalyser();
    a.fftSize = 256;
    return a;
}

/** Read normalised 0.00–1.00 level from an AnalyserNode. */
function _readLevel(analyser) {
    if (!analyser) return 0;
    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(data);
    const avg = data.reduce((sum, v) => sum + v, 0) / data.length;
    return Math.round((avg / 255) * 100) / 100;
}

/**
 * Export: Connect the local camera/mic stream to the self-audio analyser.
 * Safe to call multiple times — replaces the previous source node.
 * @param {MediaStream} stream
 */
export function connectLocalAudio(stream) {
    if (!stream || stream.getAudioTracks().length === 0) return;
    try {
        const ctx = _getAudioCtx();
        if (_selfSource) { try { _selfSource.disconnect(); } catch (_) {} }
        if (!_selfAnalyser) { _selfAnalyser = _makeAnalyser(ctx); }
        _selfSource = ctx.createMediaStreamSource(stream);
        _selfSource.connect(_selfAnalyser);
        console.debug('bactivity: Connected local audio');
    } catch (e) {
        console.warn('bactivity: Could not connect local audio:', e);
    }
}

/**
 * Export: Disconnect the local audio analyser (e.g. when mic is turned off).
 */
export function disconnectLocalAudio() {
    if (_selfSource) {
        try { _selfSource.disconnect(); } catch (_) {}
        _selfSource = null;
        console.debug('bactivity: Disconnected local audio');
    }
}

/**
 * Export: Connect a remote member's audio stream into the shared remote analyser.
 * Multiple remote sources connect to the same analyser node — they mix automatically.
 * @param {string} memberId
 * @param {MediaStream} stream
 */
export function connectRemoteAudio(memberId, stream) {
    if (!stream || stream.getAudioTracks().length === 0) return;
    try {
        const ctx = _getAudioCtx();
        // Shared remote analyser — created once, all remote sources connect to it
        if (!_remoteNodes.has('__analyser__')) {
            _remoteNodes.set('__analyser__', { analyser: _makeAnalyser(ctx) });
        }
        const remoteAnalyser = _remoteNodes.get('__analyser__').analyser;
        // Disconnect previous source for this member if any
        if (_remoteNodes.has(memberId)) {
            try { _remoteNodes.get(memberId).source.disconnect(); } catch (_) {}
        }
        const source = ctx.createMediaStreamSource(stream);
        source.connect(remoteAnalyser);
        _remoteNodes.set(memberId, { source });
        console.debug(`bactivity: Connected remote audio for ${memberId}`);
    } catch (e) {
        console.warn('bactivity: Could not connect remote audio:', e);
    }
}

/**
 * Export: Disconnect a remote member's audio source when their stream ends.
 * @param {string} memberId
 */
export function disconnectRemoteAudio(memberId) {
    if (_remoteNodes.has(memberId)) {
        try { _remoteNodes.get(memberId).source.disconnect(); } catch (_) {}
        _remoteNodes.delete(memberId);
        console.debug(`bactivity: Disconnected remote audio for ${memberId}`);
    }
}

/**
 * Export: Notify bactivity whether the local user has an active video track
 * (camera or screenshare, regardless of mute state).
 * @param {boolean} on
 */
export function setLocalVideoActive(on) {
    _localVideoActive = !!on;
}

/**
 * Export: Notify bactivity how many remote members are currently sending video
 * (camera or screenshare).
 * @param {number} count
 */
export function setRemoteVideoCount(count) {
    _remoteVideoCount = count || 0;
}

/** Take one audio sample and update the per-minute accumulators. */
function _sampleAudio() {
    _selfLevelSamples.push(_readLevel(_selfAnalyser));

    // Only read the remote analyser if at least one remote source is actually connected.
    // The shared AnalyserNode retains stale FFT buffer data after sources disconnect,
    // which would report non-zero levels even when no remote audio is present.
    const hasRemoteSources = _remoteNodes.size > 1; // >1 because '__analyser__' is always present when any remote was ever connected
    const remoteAnalyser = hasRemoteSources ? _remoteNodes.get('__analyser__')?.analyser : null;
    _remoteLevelSamples.push(_readLevel(remoteAnalyser));

    if (_localVideoActive) _selfVideoSeenThisMinute = true;
    if (_remoteVideoCount > _maxRemoteVideoThisMinute) _maxRemoteVideoThisMinute = _remoteVideoCount;
}

function _average(arr) {
    if (!arr.length) return 0;
    return Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 100) / 100;
}

/** Build and send the media heartbeat for all active channels. */
function _sendMediaHeartbeat() {
    if (activeChannelIds.size === 0) return;
    _sampleAudio(); // include a final sample in the window

    const stats = {
        self_audio_level:  _average(_selfLevelSamples),
        remote_audio_level: _average(_remoteLevelSamples),
        self_video_active:  _selfVideoSeenThisMinute,
        remote_video_count: _maxRemoteVideoThisMinute,
        window_visible:     document.visibilityState === 'visible',
    };

    // Reset accumulators for the next minute
    _selfLevelSamples       = [];
    _remoteLevelSamples     = [];
    _selfVideoSeenThisMinute  = false;
    _maxRemoteVideoThisMinute = 0;

    for (const channelId of activeChannelIds) {
        const sentViaWs = _mediaHeartbeatSender ? _mediaHeartbeatSender(channelId, stats) : false;
        if (!sentViaWs) {
            Fetch.recordBrowserActivity(channelId, 'MEDIA_HEARTBEAT', stats);
        }
    }
    console.debug('bactivity: Media heartbeat', stats);
}

function _startMonitoring() {
    if (_samplerTimer || _heartbeatTimer) return; // already running
    _samplerTimer   = setInterval(_sampleAudio,        SAMPLE_INTERVAL_MS);
    _heartbeatTimer = setInterval(_sendMediaHeartbeat,  HEARTBEAT_INTERVAL_MS);
    console.debug('bactivity: Media monitoring started');
}

function _stopMonitoring() {
    if (_samplerTimer)   { clearInterval(_samplerTimer);   _samplerTimer   = null; }
    if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; }
    _selfLevelSamples       = [];
    _remoteLevelSamples     = [];
    _selfVideoSeenThisMinute  = false;
    _maxRemoteVideoThisMinute = 0;
    console.debug('bactivity: Media monitoring stopped');
}

// ============================================================================
// Event Listeners (auto-initialize when module loads)
// ============================================================================

// beforeunload: Most reliable for detecting tab/browser closure
window.addEventListener('beforeunload', (event) => {
    if (activeChannelIds.size === 0) {
        console.debug('bactivity: No active channels, skipping beforeunload ping');
        return;
    }
    
    console.warn('bactivity: Tab/browser closing, sending TAB_BEFORE_UNLOAD');
    recordTabEvent('TAB_BEFORE_UNLOAD');
    
    // Note: preventDefault() and returnValue show "Leave site?" dialog
    // This gives more time for the keepalive request to complete
    event.preventDefault();
    event.returnValue = '';
});

// visibilitychange: Detect when tab becomes hidden/visible
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
        recordTabEvent('TAB_HIDDEN');
    } else {
        recordTabEvent('TAB_ACTIVE');
    }
});

// Page lifecycle events
window.addEventListener('load', loadLogs);
window.addEventListener('pagehide', () => { recordTabEvent('PAGE_HIDE'); });
window.addEventListener('unload', () => { 
    recordTabEvent('PAGE_UNLOAD'); 
    localStorage.setItem('lastUnloadTime', new Date().toString());
});

// bactivity.js loaded
